import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { TrendingUp, Users, BookOpen, Euro } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE'];

export default function StatisticsPage({ admin, onLogout }) {
  const [overview, setOverview] = useState(null);
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [trainingsStats, setTrainingsStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      const [overviewRes, revenueRes, trainingsRes] = await Promise.all([
        axios.get(`${API}/statistics/overview`),
        axios.get(`${API}/statistics/revenue-by-month`),
        axios.get(`${API}/statistics/registrations-by-training`)
      ]);
      
      setOverview(overviewRes.data);
      setRevenueByMonth(revenueRes.data.map(r => ({
        ...r,
        monthLabel: new Date(r.month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
      })));
      setTrainingsStats(trainingsRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!trainingsStats.length) return;
    const headers = ['Formation', 'Inscriptions totales', 'Confirmées', 'Places max', 'Taux remplissage (%)'];
    const rows = trainingsStats.map(t => [
      t.title,
      t.total_registrations,
      t.confirmed,
      t.max_participants,
      t.fill_rate.toFixed(1)
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `statistiques_formations_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <AdminLayout admin={admin} onLogout={onLogout} title="Statistiques & Analytics">
        <div className="text-center py-12">
          <p className="text-gray-500">Chargement des statistiques...</p>
        </div>
      </AdminLayout>
    );
  }

  // Pie chart data - Registration status
  const statusData = [
    { name: 'Confirmées', value: overview?.confirmed_registrations || 0 },
    { name: 'En attente', value: overview?.pending_registrations || 0 },
    { name: 'Autres', value: (overview?.total_registrations || 0) - (overview?.confirmed_registrations || 0) - (overview?.pending_registrations || 0) }
  ].filter(d => d.value > 0);

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Statistiques & Analytics">
      <div className="space-y-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-6 text-white">
            <TrendingUp size={28} className="mb-2 opacity-80" />
            <p className="text-sm opacity-90">Revenu Total</p>
            <p className="text-3xl font-bold" data-testid="stat-revenue-total">{overview?.total_revenue?.toFixed(0) || 0}€</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-xl p-6 text-white">
            <BookOpen size={28} className="mb-2 opacity-80" />
            <p className="text-sm opacity-90">Formations</p>
            <p className="text-3xl font-bold" data-testid="stat-total-trainings">{overview?.total_trainings || 0}</p>
            <p className="text-xs opacity-75 mt-1">{overview?.published_trainings || 0} publiées</p>
          </div>
          
          <div className="bg-gradient-to-br from-fuchsia-500 to-pink-600 rounded-xl p-6 text-white">
            <Users size={28} className="mb-2 opacity-80" />
            <p className="text-sm opacity-90">Apprenants</p>
            <p className="text-3xl font-bold" data-testid="stat-total-learners">{overview?.total_learners || 0}</p>
          </div>
          
          <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-6 text-white">
            <Euro size={28} className="mb-2 opacity-80" />
            <p className="text-sm opacity-90">Inscriptions</p>
            <p className="text-3xl font-bold" data-testid="stat-total-registrations-stats">{overview?.total_registrations || 0}</p>
            <p className="text-xs opacity-75 mt-1">{overview?.confirmed_registrations || 0} confirmées</p>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">📈 Évolution du Revenu</h3>
            <span className="text-sm text-gray-500">Par mois</span>
          </div>
          
          {revenueByMonth.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              Aucune donnée de revenu disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueByMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="monthLabel" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #7C3AED', borderRadius: '8px' }}
                  formatter={(value) => [`${value}€`, 'Revenu']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#7C3AED" 
                  strokeWidth={3}
                  name="Revenu (€)"
                  dot={{ fill: '#7C3AED', r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Two column charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Registration Status Pie */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100">
            <h3 className="text-xl font-bold text-gray-800 mb-6">🥧 Statut des Inscriptions</h3>
            
            {statusData.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                Aucune inscription
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Trainings Popularity */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100">
            <h3 className="text-xl font-bold text-gray-800 mb-6">🏆 Formations Populaires</h3>
            
            {trainingsStats.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                Aucune formation avec inscriptions
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trainingsStats.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis 
                    type="category" 
                    dataKey="title" 
                    stroke="#6b7280" 
                    width={140}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #7C3AED', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="total_registrations" fill="#7C3AED" name="Inscriptions" />
                  <Bar dataKey="confirmed" fill="#A78BFA" name="Confirmées" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Detailed Table */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">📋 Détails par Formation</h3>
            <button
              onClick={exportCSV}
              className="btn-violet text-sm"
              data-testid="export-csv-button"
            >
              📥 Exporter CSV
            </button>
          </div>
          
          {trainingsStats.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              Aucune formation
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-violet-100">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Formation</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Total</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Confirmées</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Places max</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Taux remplissage</th>
                  </tr>
                </thead>
                <tbody>
                  {trainingsStats.map((t) => (
                    <tr key={t.training_id} className="border-b border-gray-100 hover:bg-violet-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">{t.title}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 text-center">{t.total_registrations}</td>
                      <td className="py-3 px-4 text-sm text-green-600 text-center font-semibold">{t.confirmed}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 text-center">{t.max_participants}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-violet-500 to-purple-600 h-2 rounded-full"
                              style={{ width: `${Math.min(t.fill_rate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-violet-600 w-12">{t.fill_rate.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
